/* eslint-disable max-lines */
import { Fragment, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Collapse,
  Grid,
  IconButton,
  Link,
  Pagination,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import ShippingIcon from '@mui/icons-material/LocalShipping';
import { visuallyHidden } from '@mui/utils';
import { capitalize, isEmpty, isNil, map, toLower } from 'lodash';
import { differenceInDays, differenceInHours, format } from 'date-fns';

import LoadingModule from 'common/components/LoadingModule';
import NothingFound from 'common/components/NothingFound';
import perfectRxTheme from 'common/theme/perfectRxTheme';
import { getTrackingInfo } from 'common/utils';
import BatchShipmentDialog from 'common/modules/BatchShipmentDialog';
import { selectAccessibleFacilities } from '../../../store/selectors/shipmentModuleSelectors';
import { selectShipstationCurrentPageItems } from '../../../store/selectors/shipstationShipmentSelectors';
import {
  fetchShipstationShipments,
  updateShipstationSortBy,
} from '../../../store/thunks/shipstationShipmentThunks';
import { getStateDisplayName } from '../../../store/utils/shipmentUtils';

const PAGE_SIZE = 25;

const baseHeadCells = [
  { id: 'expand', label: '', sortable: false },
  { id: 'groupKey', label: 'Order', sortable: false },
  { id: 'createdAt', label: 'Age', sortable: true },
  { id: 'orderDate', label: 'Order Date', sortable: false },
  { id: 'itemCount', label: '# of Items', sortable: false },
  { id: 'recipientName', label: 'Recipient', sortable: false },
  { id: 'partner', label: 'Partner', sortable: false },
  { id: 'practice', label: 'Practice', sortable: false },
  { id: 'fulfillingPharmacy', label: 'Facility', sortable: false },
  { id: 'trackingNumber', label: 'Tracking Number', sortable: true, shippedOnly: true },
  { id: 'batchShipment', label: '', sortable: false },
];

const ShipstationShipmentTable = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const [expandedRows, setExpandedRows] = useState(new Set());
  const [openBatchDialog, setOpenBatchDialog] = useState(false);
  const [currentBatchShipmentId, setCurrentBatchShipmentId] = useState(null);

  const { sortBy, count, loading, currentPage } = useSelector(
    ({ shipstationShipment }) => shipstationShipment.list
  );
  const { activeCategory } = useSelector(({ shipstationShipment }) => shipstationShipment);
  const orders = useSelector(selectShipstationCurrentPageItems);

  const headCells =
    activeCategory === 'awaitingShipment'
      ? baseHeadCells.filter((c) => !c.shippedOnly)
      : baseHeadCells;
  const facilities = useSelector(selectAccessibleFacilities);

  const totalPages = Math.ceil(count / PAGE_SIZE);

  const handleSort = (event) => {
    const sortKey = event.currentTarget?.dataset?.headProperty;
    dispatch(
      updateShipstationSortBy({
        sortBy: {
          sortKey,
          sortOrder: sortBy.sortKey === sortKey && sortBy.sortOrder === 'ASC' ? 'DESC' : 'ASC',
        },
      })
    );
  };

  const handlePageChange = (_, page) => {
    if (currentPage === page) return;
    dispatch(fetchShipstationShipments({ page }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const toggleRow = (groupKey) => {
    setExpandedRows((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(groupKey)) {
        newSet.delete(groupKey);
      } else {
        newSet.add(groupKey);
      }
      return newSet;
    });
  };

  const handleOpenBatchDialog = (batchShipmentId) => {
    setCurrentBatchShipmentId(batchShipmentId);
    setOpenBatchDialog(true);
  };

  const handleCloseBatchDialog = () => {
    setCurrentBatchShipmentId(null);
    setOpenBatchDialog(false);
  };

  const getAgeColor = (ageInHours) => {
    if (ageInHours === null || ageInHours === undefined) return theme.palette.text.secondary;
    if (ageInHours > 48) return theme.palette.error.main;
    if (ageInHours > 24) return theme.palette.warning.main;
    return theme.palette.text.primary;
  };

  if (loading && isEmpty(orders)) {
    return (
      <Grid item>
        <LoadingModule />
      </Grid>
    );
  }

  return (
    <Grid container direction='column'>
      <BatchShipmentDialog
        batchShipmentId={currentBatchShipmentId}
        onClose={handleCloseBatchDialog}
        open={openBatchDialog}
      />

      <Grid item>
        <TableContainer>
          <Table sx={{ minWidth: 1100 }}>
            <TableHead>
              <TableRow>
                {headCells.map((headCell) => (
                  <TableCell
                    key={headCell.id}
                    align='center'
                    sx={{ py: 1.5, px: 1, fontSize: '0.875rem' }}
                  >
                    {headCell.sortable ? (
                      <TableSortLabel
                        active={sortBy.sortKey === headCell.id}
                        direction={
                          sortBy.sortKey === headCell.id ? toLower(sortBy.sortOrder) : 'asc'
                        }
                        onClick={handleSort}
                        data-head-property={headCell.id}
                        sx={{ justifyContent: 'center', width: '100%' }}
                      >
                        <Typography variant='subtitle2'>{headCell.label}</Typography>
                        {sortBy.sortKey === headCell.id ? (
                          <Box component='span' sx={visuallyHidden}>
                            {toLower(sortBy.sortOrder) === 'desc'
                              ? 'sorted descending'
                              : 'sorted ascending'}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    ) : (
                      <Typography variant='subtitle2'>{headCell.label}</Typography>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {map(
                orders,
                ({
                  groupKey,
                  isBatchShipment,
                  batchShipmentId,
                  trackingNumber,
                  recipientName,
                  fulfillingPharmacyId,
                  partner,
                  practice,
                  createdAt,
                  itemCount,
                  lineItems,
                }) => {
                  const orderDate = format(new Date(createdAt), 'MM/dd/yyyy');
                  const ageInHours = differenceInHours(new Date(), new Date(createdAt));
                  const age =
                    ageInHours > 48
                      ? `${differenceInDays(new Date(), new Date(createdAt))}days`
                      : `${ageInHours}h`;
                  const ageColor = getAgeColor(ageInHours);
                  const isExpanded = expandedRows.has(groupKey);
                  const fillingFacility = facilities.find(
                    (f) => f.fulfillingPharmacyId === fulfillingPharmacyId
                  );
                  const partnerName = partner?.partnerName || partner?.partnerUUID || '-';
                  const practiceName = practice?.practiceName || practice?.practiceId || '-';

                  return (
                    <Fragment key={groupKey}>
                      <TableRow hover={false} sx={{ backgroundColor: 'inherit' }}>
                        <TableCell sx={{ py: 1.5, width: 50 }}>
                          <IconButton
                            aria-label={`Toggle Details ${groupKey}`}
                            size='small'
                            onClick={() => toggleRow(groupKey)}
                          >
                            {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                          </IconButton>
                        </TableCell>

                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography
                            variant='body2'
                            sx={{ fontWeight: 500, fontFamily: 'monospace' }}
                          >
                            {groupKey}
                          </Typography>
                        </TableCell>

                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2' sx={{ color: ageColor }}>
                            {age}
                          </Typography>
                        </TableCell>

                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{orderDate}</Typography>
                        </TableCell>

                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{itemCount}</Typography>
                        </TableCell>

                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2' sx={{ fontWeight: 500 }}>
                            {recipientName || '-'}
                          </Typography>
                        </TableCell>

                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{capitalize(partnerName)}</Typography>
                        </TableCell>

                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>{capitalize(practiceName)}</Typography>
                        </TableCell>

                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          <Typography variant='body2'>
                            {fillingFacility?.name && fillingFacility?.state
                              ? `${fillingFacility.name} (${getStateDisplayName(fillingFacility.state)})`
                              : fillingFacility?.name || '-'}
                          </Typography>
                        </TableCell>

                        {activeCategory !== 'awaitingShipment' && (
                          <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                            {!trackingNumber && <Typography variant='body2'>-</Typography>}
                            {trackingNumber && (
                              <Link
                                href={getTrackingInfo({ trackingNumber }).trackingUrl}
                                target='_blank'
                                sx={{ fontSize: '1em' }}
                              >
                                {trackingNumber}
                              </Link>
                            )}
                          </TableCell>
                        )}

                        <TableCell align='center' sx={{ py: 1.5, px: 1 }}>
                          {isBatchShipment && batchShipmentId && (
                            <Tooltip
                              title='Shipment created as part of a batch'
                              arrow
                              placement='top'
                            >
                              <ShippingIcon
                                onClick={() => handleOpenBatchDialog(batchShipmentId)}
                                sx={{
                                  cursor: 'pointer',
                                  color: perfectRxTheme.palette.success.main,
                                  fontSize: '1.5rem',
                                  '&:hover': {
                                    transform: 'scale(1.1)',
                                    transition: 'transform 0.2s',
                                  },
                                }}
                              />
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>

                      {/* Expanded row: line items detail */}
                      <TableRow>
                        <TableCell
                          colSpan={headCells.length}
                          sx={{ p: 0, border: 'none', backgroundColor: 'inherit' }}
                        >
                          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Collapse
                              in={isExpanded}
                              timeout='auto'
                              unmountOnExit
                              sx={{ width: '100%' }}
                            >
                              {lineItems && lineItems.length > 0 && (
                                <Box
                                  sx={{
                                    py: 2,
                                    px: 3,
                                    width: '100%',
                                    backgroundColor: theme.palette.grey[50],
                                  }}
                                >
                                  <Table size='small'>
                                    <TableHead>
                                      <TableRow>
                                        {['Drug Name', 'Order ID', 'SKU', 'Quantity'].map(
                                          (header) => (
                                            <TableCell
                                              key={header}
                                              align='center'
                                              sx={{
                                                fontWeight: 600,
                                                backgroundColor: theme.palette.grey[100],
                                                borderBottom: `1px solid ${theme.palette.divider}`,
                                                py: 1,
                                                px: 1,
                                              }}
                                            >
                                              {header}
                                            </TableCell>
                                          )
                                        )}
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {map(lineItems, (li, idx) => (
                                        <TableRow
                                          key={li.lineItemId || `${li.orderShipmentId}-${idx}`}
                                          hover={false}
                                          sx={{ backgroundColor: 'inherit' }}
                                        >
                                          <TableCell align='center' sx={{ py: 1, px: 1 }}>
                                            <Typography variant='body2'>
                                              {li.drugName || '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='center' sx={{ py: 1, px: 1 }}>
                                            <Typography
                                              variant='body2'
                                              sx={{ fontFamily: 'monospace' }}
                                            >
                                              {li.orderId || '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='center' sx={{ py: 1, px: 1 }}>
                                            <Typography
                                              variant='body2'
                                              sx={{ fontFamily: 'monospace' }}
                                            >
                                              {li.sku || '-'}
                                            </Typography>
                                          </TableCell>
                                          <TableCell align='center' sx={{ py: 1, px: 1 }}>
                                            <Typography variant='body2'>
                                              {li.quantity || '-'}
                                            </Typography>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </Box>
                              )}
                            </Collapse>
                          </Box>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                }
              )}
            </TableBody>

            {!isNil(orders) && !isEmpty(orders) && totalPages > 1 && (
              <TableFooter>
                <TableRow>
                  <TableCell colSpan={headCells.length} align='right' sx={{ borderBottom: 'none' }}>
                    <Pagination
                      sx={{
                        justifyContent: 'flex-end',
                        alignItems: 'flex-end',
                        display: 'flex',
                      }}
                      count={totalPages}
                      page={currentPage}
                      size='small'
                      onChange={handlePageChange}
                    />
                  </TableCell>
                </TableRow>
              </TableFooter>
            )}
          </Table>
        </TableContainer>
      </Grid>

      {!isNil(orders) && isEmpty(orders) && !loading && (
        <Grid item>
          <NothingFound />
        </Grid>
      )}
    </Grid>
  );
};

export default ShipstationShipmentTable;
